import { useCallback, useEffect } from 'react'
import { Loader2, WifiOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, useParams } from 'react-router'
import { achatNumeroEnAttente, choixNumeroARefaire, type CompanyInfo } from '@/api/auth'
import { defaultLocale, isLocale } from '@/lib/locales'
import { useFirstCompany, useProfile } from '@/api/profile'
import { usePaymentMethod } from '@/api/stripe'
import { PasskeyNudge } from '@/components/auth/PasskeyNudge'
import { BandeauConnexion } from '@/components/BandeauConnexion'
import { MacUpdateNotifier } from '@/components/auth/MacUpdateNotifier'
import { TrialPaymentGate } from '@/components/auth/TrialPaymentGate'
import { useConnexionStore } from '@/stores/useConnexionStore'

/**
 * Guard placé entre AuthGuard et les routes applicatives (dashboard, manager).
 *
 * Un user authentifié peut techniquement taper `/dashboard` dans l'URL même
 * sans avoir fini son onboarding. Ce guard le renvoie vers la BONNE étape
 * d'onboarding selon l'état réel de sa company (même logique que
 * `usePostLoginRedirect.routeManager`).
 *
 *  - AGENT : passe direct (pas d'onboarding requis — c'est le manager qui
 *    setup la company. Un agent invité dans une company configurée arrive
 *    direct sur son dashboard).
 *  - MANAGER : selon l'état company → step1, step2, subscribe.
 *
 * `/subscribe`, `/checkout`, `/onboarding/*` ne sont PAS derrière ce guard
 * (l'onboarding y mène, l'user doit pouvoir payer même sans numéro actif).
 */
export function OnboardingGuard() {
  const { locale } = useParams<{ locale: string }>()
  const lng = isLocale(locale) ? locale : defaultLocale
  const profileQuery = useProfile()
  const companiesQuery = useFirstCompany()
  const { data: profile, refetch: refetchProfile } = profileQuery
  const { data: company, refetch: refetchCompanies } = companiesQuery
  const injoignable = useConnexionStore((s) => s.injoignable)

  // Check carte UNIQUEMENT pour le groupe à risque : un manager en essai, sans
  // numéro. C'est le seul cas où une sub `trialing` sans carte peut exister.
  // `enabled` togglé → aucune requête Stripe pour les autres users.
  const needsCardCheck =
    profile?.role !== 'AGENT' &&
    !!company?.is_trialing &&
    !company?.has_active_number &&
    !company?.phone_number
  const {
    data: paymentMethod,
    isLoading: pmLoading,
    isError: pmError,
  } = usePaymentMethod({ enabled: needsCardCheck })

  const reessayer = useCallback(() => {
    void refetchProfile()
    void refetchCompanies()
  }, [refetchProfile, refetchCompanies])

  // On ne décide rien tant que le profil ET les entreprises n'ont pas été lus.
  // « Pas de réponse » n'est pas « nouveau client » : une requête en échec
  // peut rester en attente (React Query suspend le réessai d'un onglet en
  // arrière-plan), sans données ni erreur. Le 28 sept 2026, un client payant
  // a été envoyé à l'onboarding sur une rafale de 429.
  // Pendant le réessai, l'écran « Callizy ne répond pas » seulement pour une vraie
  // panne (l'intercepteur la reconnaît : pas de réponse, 429, 502-504) qui dure au
  // moins deux essais. Un 401 le temps que le jeton arrive à la connexion, ou un 502
  // isolé, gardent le simple chargement : vu le 20 oct. 2026, l'écran s'affichait à
  // la connexion alors que le réessai réussissait une seconde plus tard.
  const enEchec = (q: { isPending: boolean; isError: boolean; failureCount: number; fetchStatus: string }) =>
    q.isError || (q.isPending && (q.fetchStatus === 'paused' || (injoignable && q.failureCount >= 2)))
  if ((enEchec(profileQuery) && !profile) || (enEchec(companiesQuery) && !company)) {
    return <ServeurInjoignable onRetry={reessayer} />
  }

  if (profileQuery.isPending || companiesQuery.isPending || (needsCardCheck && pmLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    )
  }

  const protectedOutlet = (
    <>
      <BandeauConnexion />
      <Outlet />
      <PasskeyNudge />
      <MacUpdateNotifier />
    </>
  )

  // Cellulaire à vérifier (compte récent, gestionnaire ou membre invité) : avant tout le reste.
  if (profile?.phone_verification_required) {
    return <Navigate to={`/${lng}/onboarding/phone`} replace />
  }

  if (profile?.role === 'AGENT') return protectedOutlet

  // Numéro acheté mais mise en service pas finie (jusqu'à l'adresse 911) : on
  // reprend à l'écran suivant. Les entreprises d'avant la mise en service sont
  // marquées terminées par la migration ; un serveur plus ancien n'envoie pas
  // le champ (undefined) et ne ramène donc personne.
  if (
    (company?.has_active_number || company?.phone_number) &&
    company?.mise_en_service_terminee_at === null
  ) {
    return <Navigate to={`/${lng}/onboarding/${ecranSuivant(company.mise_en_service_etape)}`} replace />
  }

  // Manager onboarding terminé (numéro actif) → laisse passer
  if (company?.has_active_number || company?.phone_number) return protectedOutlet

  // Manager en essai SANS numéro et SANS carte → gate bloquant (ajout de carte
  // in-app). Ferme le bypass "j'ai démarré l'essai mais abandonné la carte".
  // Fail-open : si la requête Stripe a échoué, on laisse passer — le backend
  // (purchase_number) reste le filet de sécurité contre l'achat sans carte.
  if (
    needsCardCheck &&
    !pmError &&
    paymentMethod?.has_payment_method === false
  ) {
    return <TrialPaymentGate />
  }

  // Payé mais numéro pas encore acheté : on reprend à l'écran Bienvenue, qui l'achète.
  if (achatNumeroEnAttente(company)) {
    return <Navigate to={`/${lng}/onboarding/welcome`} replace />
  }

  // Paid, but the chosen number could not be bought: choose another one.
  if (choixNumeroARefaire(company)) {
    return <Navigate to={`/${lng}/onboarding/step1?indisponible=1`} replace />
  }

  // Manager qui a une subscription valide mais plus de numéro (a supprimé son
  // numéro, ou annulé son abo avec grâce de fin de période). Il a déjà fait
  // l'onboarding, il a accès à toutes les pages — c'est juste à lui de
  // racheter un numéro depuis l'onglet Numéros s'il en a besoin.
  if (company?.has_active_subscription) return protectedOutlet

  // Sinon : redirige vers l'étape exacte où l'user en est
  return <Navigate to={getOnboardingTarget(company, lng)} replace />
}

/** L'écran de mise en service qui suit le dernier enregistré. */
function ecranSuivant(etape: string | undefined): string {
  const suite: Record<string, string> = { langue: 'reponse', reponse: 'options', options: 'urgence' }
  return suite[etape || ''] || 'langue'
}

/** Même logique que `usePostLoginRedirect.routeManager`, factorisée. */
function getOnboardingTarget(
  company: CompanyInfo | undefined,
  locale: string,
): string {
  if (!company?.preferred_phone_number) {
    return `/${locale}/onboarding/step1`
  }
  const hasStep2 =
    company.name &&
    !company.name.startsWith('Nouvelle entreprise')
  // L'adresse 911 n'est plus demandée avant le paiement : elle vient à la mise en service.
  if (!hasStep2) return `/${locale}/onboarding/step2`
  return `/${locale}/subscribe`
}

function ServeurInjoignable({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  // Réessai invisible, de plus en plus espacé (10, 20, 40, puis 60 s) avec un
  // décalage aléatoire : pendant une vraie panne, les clients ouverts ne
  // frappent pas tous le serveur au même instant. Aucun délai affiché.
  useEffect(() => {
    let essai = 0
    let minuteur: number
    const planifier = () => {
      const base = Math.min(10_000 * 2 ** essai, 60_000)
      minuteur = window.setTimeout(() => {
        essai += 1
        onRetry()
        planifier()
      }, base * (0.8 + Math.random() * 0.4))
    }
    planifier()
    return () => window.clearTimeout(minuteur)
  }, [onRetry])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-slate-500/10 flex items-center justify-center">
          <WifiOff className="h-6 w-6 text-slate-500" />
        </div>
        <h1 className="text-lg font-semibold text-foreground">
          {t('serveurInjoignable.title', 'Callizy ne répond pas pour le moment')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('serveurInjoignable.description', "Rien n'a été modifié dans votre compte. Nous réessayons automatiquement.")}
        </p>
        <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t('serveurInjoignable.reconnexion', 'Reconnexion…')}
        </p>
        {/* Courriel : le chat et la ligne de support passent peut-être par Callizy. */}
        <p className="text-sm text-muted-foreground">
          {t('serveurInjoignable.support', 'Si le problème persiste, écrivez-nous à')}{' '}
          <a href="mailto:support@callizy.ca" className="font-medium text-blue-600 hover:underline">
            support@callizy.ca
          </a>
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="text-sm font-medium text-blue-600 hover:underline"
        >
          {t('serveurInjoignable.retry', 'Réessayer maintenant')}
        </button>
      </div>
    </div>
  )
}
